import { useEffect, useCallback, FormEvent, useState, KeyboardEvent, useRef, MouseEvent } from 'react';

require('./RegCodeInput.css');

export type Props = {
    id?: string;
    value: string;
    disabled?: boolean;
    onChange(value: string): void;
};

const emptyParts = [ '', '', '' ];

export function parseRegCode(input: string): string[] {
    return input ? input.split('-').map(s => s.toLowerCase().trim()) : emptyParts;
}

export function makeRegCode(...parts: string[]): string {
    return parts.join('-');
}

export const RegCodeInput = ({ id = 'reg-code', value, disabled = false, onChange }: Props) => {
    const input1Ref = useRef<HTMLInputElement>(null);
    const input2Ref = useRef<HTMLInputElement>(null);
    const input3Ref = useRef<HTMLInputElement>(null);
    
    const [ part1, part2, part3 ] = parseRegCode(value);

    const [ value1, setValue1 ] = useState(part1);
    const [ value2, setValue2 ] = useState(part2);
    const [ value3, setValue3 ] = useState(part3);

    // Fire onChange event whenever the individual parts have changed
    useEffect(() => {
        const regCode = makeRegCode(value1, value2, value3);
        onChange(regCode);
    }, [ value1, value2, value3, onChange ]);

    // Set focus to the first incomplete input field if the outer element is clicked
    const handleClickDiv = useCallback(() => {
        for (const ref of [ input1Ref, input2Ref, input3Ref ]) {
            const input = ref.current;
            if (input && input.value.length < 4) {
                input.focus();
                return;
            }
        }
    }, [ input1Ref, input2Ref, input3Ref ]);

    // Don't propagate clicks up to the parent container
    const handleClickInput = useCallback((e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
    }, []);


    // Receive change events for the three input controls and set our internal values
    const handleChange = useCallback((e: FormEvent<HTMLInputElement>) => {
        const { nextSibling, previousSibling } = e.currentTarget;
        const value = e.currentTarget.value.replace(/[^a-f0-9]/i, '');
        if (!previousSibling) {
            setValue1(value);
        }
        else if (!nextSibling) {
            setValue3(value);
        }
        else {
            setValue2(value);
        }
    }, [ setValue1, setValue2, setValue3 ]);

    // Advance to the next field when the current field becomes full
    const handleKeyUp = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        const { value, nextSibling, previousSibling } = e.currentTarget;
        if (!previousSibling) {
            if (value.length >= 4 && /[a-f0-9]/i.test(e.key)) {
                input2Ref.current && input2Ref.current.focus();
            }
        }
        else if (nextSibling) {
            if (value.length >= 4 && /[a-f0-9]/i.test(e.key)) {
                input3Ref.current && input3Ref.current.focus();
            }
        }
    }, [ input2Ref, input3Ref ]);

    // Update our internal representation any time the input value changes
    useEffect(() => {
        const [ part1, part2, part3 ] = parseRegCode(value);
        setValue1(part1);
        setValue2(part2);
        setValue3(part3);
    }, [ value, setValue1, setValue2, setValue3 ]);

    return (
        <div
            id={ id }
            className={ `reg-code-input${disabled ? ' disabled' : ''}` }
            onClick={ handleClickDiv }
        >
            <input
                type="text"
                ref={ input1Ref }
                spellCheck={ false }
                disabled={ disabled }
                autoComplete="off"
                onChange={ handleChange }
                onKeyUp={ handleKeyUp }
                onClick={ handleClickInput }
                value={ value1 }
                maxLength={ 4 }
                autoFocus={ !value1 }
            />

            <span className="separator">-</span>
            
            <input
                type="text"
                ref={ input2Ref }
                spellCheck={ false }
                disabled={ disabled }
                autoComplete="off"
                onChange={ handleChange }
                onKeyUp={ handleKeyUp }
                onClick={ handleClickInput }
                value={ value2 }
                maxLength={ 4 }
            />
            
            <span className="separator">-</span>
            
            <input
                type="text"
                ref={ input3Ref }
                spellCheck={ false }
                disabled={ disabled }
                autoComplete="off"
                onChange={ handleChange }
                onClick={ handleClickInput }
                value={ value3 }
                maxLength={ 4 }
                autoFocus={ !!value3 }
            />
        </div>
    );
};
